import useSWRMutation from "swr/mutation";
import { XIcon } from "lucide-react";
import { mutateRequest, optimisticPrd, putFetcherKeyed, writeAtom } from "@/lib/swr";
import { useToast } from "@/hooks/use-toast";
import { toastError } from "@/lib/toast";
import { useUsersByIds } from "@/hooks/users";
import { Button } from "@/components/ui/button";
import UsersSelector from "@/components/users-selector";
import type { PickerUser } from "@/components/user-selector";
import { UserChip } from "@/components/user-chip";
import { AddButton } from "./add-button";
import type { Prd } from "@/types/domain";

// PRD-level viewer list (WED-274): members can read the PRD regardless of app
// binding. Editing is decided by owner / creator and linked-App membership,
// not by this list. The server is the rule — this only mirrors `prd.canEdit`
// to decide whether the add / remove controls render.
export const PrdMembersEditor = ({
  prd,
  readOnly,
}: {
  prd: Prd;
  readOnly?: boolean;
}) => {
  const { toast } = useToast();
  const members = useUsersByIds(prd.memberIds);
  // The PUT echoes the full PRD: write it back instead of refetching; a
  // failure reloads the detail to undo the optimistic write (WED-249).
  const { trigger, isMutating } = useSWRMutation(
    `/prds/${prd.id}#members`,
    putFetcherKeyed<Prd>,
    {
      onSuccess: (saved) => writeAtom(`/prds/${prd.id}`, saved),
      onError: (e: unknown) => {
        mutateRequest(`/prds/${prd.id}`);
        toastError(toast, "操作失败", e, "提交失败");
      },
    }
  );

  const save = (memberIds: string[]) => {
    optimisticPrd(prd.id, { memberIds });
    trigger({ memberIds });
  };

  const onPick = (users: PickerUser[]) => save(users.map((u) => String(u.id)));
  const remove = (id: string) =>
    save((prd.memberIds ?? []).filter((m) => m !== id));

  return (
    <div className="flex min-w-0 flex-col items-start gap-2">
      {members.map((u) => (
        <div key={u.id} className="flex min-w-0 items-center gap-2">
          <UserChip user={u} className="max-w-40" />
          {!readOnly && (
            <Button
              variant="ghost"
              size="icon"
              title="移除成员"
              aria-label="移除成员"
              disabled={isMutating}
              onClick={() => remove(u.id)}
              className="h-6 w-6 text-muted-foreground hover:text-destructive"
            >
              <XIcon className="h-3.5 w-3.5" />
            </Button>
          )}
        </div>
      ))}
      {!readOnly && (
        <div>
          <UsersSelector
            selectedUsers={members as PickerUser[]}
            onContinue={onPick}
            trigger={<AddButton title="添加成员" />}
          />
        </div>
      )}
    </div>
  );
};
